import { css } from '@emotion/core'
import { NoticeQuestion, NoticeWarning } from '@fs/zion-icon'
import { type Card, colors } from '@fs/zion-ui'
import { type ComponentProps } from 'react'
import { type PersonType } from './types/general'
import { type MergeAnalysisConstraint, type MergeColor } from './types/mergeAnalysis'
import { type HeightDiv } from './providers/HeightProvider'

/** Because the resultant column has two borders and survivor and duplicate only have 1 border each plus a gap size of 1px, those columns have 0.5px extra content space, so we add that amount to the start of resultant column and end of arrow column to resolve the difference. */
export const MERGE_DIFF_PX_AMOUNT = '0.5px'

/** Centers the arrow icon vertically between the two person columns in sticky headers */
export const arrowCss = css`
  display: flex;
  height: 100%;
  align-items: center;
  margin-inline-end: ${MERGE_DIFF_PX_AMOUNT};
  padding: 0 2px;
`

// If this is sm or bigger, update hiddenArrowCss margin and start calculations in MergeGrid.tsx
export const MERGE_GAP = 'xxs'

export const mergeInconsistenciesFieldMap = {
  red: {
    cardColor: colors.danger03,
    iconColor: colors.danger60,
    borderColor: colors.danger60,
    Icon: NoticeWarning,
  },
  yellow: {
    cardColor: colors.yellow03,
    iconColor: colors.yellow40,
    borderColor: colors.yellow40,
    Icon: NoticeQuestion,
  },
  newDesign: {
    red: {
      cardColor: colors.danger05,
      iconColor: colors.danger60,
      borderColor: colors.danger60,
      Icon: NoticeWarning,
    },
    yellow: {
      cardColor: colors.yellow02,
      iconColor: colors.yellow60,
      borderColor: colors.yellow60,
      Icon: NoticeQuestion,
    },
  },
}

export const MERGE_LAYOUT_MARGIN = 'sm'

export const personTypeMap = {
  duplicate: {
    cardBackgroundColor: colors.gray02,
    cardEdges: 'none',
    headingKey: 'tree-person.duplicate-person.heading',
    newHeadingKey: 'tree-person.duplicate-person.heading',
  },
  survivor: {
    cardBackgroundColor: colors.gray00,
    cardEdges: 'border',
    headingKey: 'tree-person.surviving-person.heading',
    newHeadingKey: 'tree-person.main-person.heading',
  },
  resultant: {
    cardBackgroundColor: colors.transparent.blue02,
    cardEdges: 'border',
    headingKey: 'tree-person.resultant-person.heading',
    newHeadingKey: 'tree-person.final-result.heading',
  },
} satisfies Record<
  PersonType,
  {
    cardBackgroundColor: string
    headingKey: string
    newHeadingKey: string
    cardEdges: ComponentProps<typeof Card>['edges']
  }
>

export function getMergeScoreColor(
  mergeConstraint: undefined,
  mergePersonScore: number | undefined
): Exclude<MergeColor, 'redConstraint'>
export function getMergeScoreColor(
  mergeConstraint: MergeAnalysisConstraint,
  mergePersonScore: number | undefined
): MergeColor
export function getMergeScoreColor(
  mergeConstraint: MergeAnalysisConstraint | undefined,
  mergePersonScore: number | undefined
): MergeColor | Exclude<MergeColor, 'redConstraint'>
export function getMergeScoreColor(
  mergeConstraint: MergeAnalysisConstraint | undefined,
  mergePersonScore: number | undefined
): MergeColor {
  if (mergeConstraint && mergeConstraint !== 'CAN_MERGE_ANY_ORDER' && mergeConstraint !== 'CAN_MERGE_THIS_ORDER_ONLY')
    return 'redConstraint'
  if (mergePersonScore !== undefined && mergePersonScore <= 0.05) return 'redScore'
  if (mergePersonScore !== undefined && mergePersonScore < 1) return 'yellow'
  return 'green'
}

export const personTypeToHeightTypeMap: Record<PersonType, ComponentProps<typeof HeightDiv>['type']> = {
  duplicate: 'compare1',
  survivor: 'compare2',
  resultant: 'compare3',
}

export const personTypeToHighlightTypeMap = {
  duplicate: 'highlight1',
  survivor: 'highlight2',
} as const
